import "./data-visualization.css"
import { useEffect, useState } from "react";
import { Table } from "@/features/data/components/table/Table";
import Drawer from "@/components/drawer/Drawer";
import Modal from "@/components/modal/Modal";
import { Tag } from "@/components/tag/Tag";
import { Dropdown } from "@/components/dropdown";
import { Button } from "@/components/button";
import { EntityForm } from "../entity-form";
import Pagination from "@/components/pagination/Pagination";
import type { Column } from "@/types/array.types";
import Card from "@/components/card/Card";
import { PAGE_SIZE, type StatsType } from "@/features/data/view-model/viewModel";
import { ExportData } from "../export-data/ExportData";
import { Icon } from "@/components/icon";
import { InputText } from "@/components/input-text";

type TagType = {
  key: string,
  value: string,
  label: string
}

type SortOption = {
  key: string;
  label: string;
  by: "croissant" | "decroissant"
};

interface DataVisualizationProps<T> {
  columns: Column<T>[];
  columnsExport: Column<T>[];
  count: number;
  filters: Column<T>[];
  filterData: any;
  data: any[];
  getData: (page: number,  pageSize: number, sortOption?: SortOption, tags?: TagType[], search?: string) => void;
  table: string;
  title: string;
  stats: StatsType;
  sortOptions: SortOption[];
  role: string;
  onCreate: (values: Partial<T>) => void;
  onExportData: (columns: string[], name?: string, sort?: SortOption, tags?: TagType[]) => void;
  onDelete: (id: string) => void;
  onUpdate: (values: T, id: string) => void;
}

function DataVisualization<T extends Record<string, any>> ({
    columns, 
    columnsExport,
    count, 
    data, 
    filters, 
    filterData, 
    role, 
    table, 
    title, 
    sortOptions, 
    stats, 
    getData, 
    onExportData, 
    ...props}: DataVisualizationProps<T>) 
  {

    const [maxElements, setMaxElements] = useState(PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<number>(null);
    const [tags, setTags] = useState([] as TagType[]);
    const [open, setOpen] = useState(false);
    const [isDeleteOpen, setDeleteOpen] = useState(false);  
    const [isExportOpen, setIsExportOpen] = useState(false);  
    const [uuid, setUuid] = useState("");
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<SortOption | null>(null);
    const totalPages = Math.ceil(count / maxElements);

    const pageSizes = ["15", "50", "100", "Tout afficher"];
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search);
        }, 400);

        return () => clearTimeout(timeout);
    }, [search]); 
    
    useEffect(() => {
      getData(page, maxElements, sort || undefined, tags,
        debouncedSearch);
    }, [page, maxElements, sort, tags,
      debouncedSearch]);
    
    useEffect(() => {
      window.scrollTo({top: 0, left: 0, behavior: "smooth"})
  }, [page]);

    const onAdd = () => {
      setSelectedRow(null);
      setOpen(true);
    };

    const onCancel = () => {
      setSelectedRow(null);
      setOpen(false);
    };

    const onDelete = async (id: string) => { 
      setIsLoading(true);
      await props.onDelete(id);
      await getData(page, maxElements, sort || undefined, tags);
      setIsLoading(false);
      setDeleteOpen(false);
      setOpen(false);
      setSelectedRow(null);
    };

    
    const onEdit = (id) => {
      setSelectedRow(id);
      setOpen(true);
    };
    
    const onFilter = (filter: Column<T>, value: string) => {
      const currentTag = {
        key: filter.key as string,
        value: value,
        label: filter.renderLabel ? filter.renderLabel(value) : value,
      };

  
      setTags((prev) => {
        const alreadyExists = prev.some(
          (t) => t.key === currentTag.key && t.value === currentTag.value
        );
        
      
        if (alreadyExists) return prev;
        
        return [...prev, currentTag];
      });
    
      setSort(null);

    };

  const onSort = async (value: string) => {
      setTags([]);
      const currentSortOption = sortOptions.find(
        (o) => o.label === value
      );
      if (!currentSortOption) return;
      setSort(currentSortOption);
      getData(page, maxElements, currentSortOption);
  }

  const onSubmit = async (values: Partial<T>) => {
    setIsLoading(true);
    await props.onCreate(values);
    await getData(page, maxElements, sort || undefined, tags);
    setIsLoading(false);
    setOpen(false);
  };

  const onUpdate = async (values) => {
    setIsLoading(true);
    await props.onUpdate(values, data[selectedRow]["id"]);
    await getData(page, maxElements, sort || undefined, tags);
    setIsLoading(false);
    setSelectedRow(null);
    setOpen(false);
  }


  
      return (
        <div className="data-visualization">  
          <h1>{title}</h1>
          <div className="data-visualization-header">
              <div className="dashboard-grid">
                  <Card
                      title="Biens"
                      value={stats.total_assets}
                  />
                  <Card
                      title="Valeur totale"
                      value={`${new Intl.NumberFormat("it-IT").format(stats.total_value)}€`}
                  />
              </div>
          </div>
          <InputText
            className="data-visualization-search"
            name="search"
            label="Rechercher"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
          <div className="data-visualization-table-top">
              
                <div className="data-visualization-btn">
                      <Button label={"Exporter les données"} leftIcon="document" onClick={() => setIsExportOpen(true)} variant="secondary"></Button>
                      <ExportData 
                        isOpen={isExportOpen} 
                        options={columnsExport} 
                        tags={!!tags.length}
                        onCancel={() => setIsExportOpen(false)} 
                        onExport={(columns, name, exportSelection) => {
                          const selection = exportSelection ? tags : null
                          onExportData(columns, name, sort, selection)
                          setIsExportOpen(false)}}/>
                      {(role == "editor" || role == "admin")  && <Button label={"Ajouter une nouvelle oeuvre"} leftIcon="add" onClick={onAdd}></Button>}
                  </div>
                  <div className="data-visualization-filters">
                <div className="data-visualization-filters-dropdown">
                  {filters?.map((filter) => (
                      <Dropdown
                          key={filter.key as string} 
                          label={filter.label as string ?? filter.key as string} 
                          options={filterData[filter.key] ?? []}
                          value={tags.find(t => t.key === filter.key)?.value ?? ""}  
                          variant="primary"
                          onChange={(value) => 
                           onFilter(filter, value)}
                           renderLabel={filter.renderLabel} 
                      />
                      ))}
                      <Dropdown
                        label="Trier par"
                        options={sortOptions.map((o) => 
                          o.label
                        )}
                        value={""}
                        onChange={(value) =>
                         onSort(value)
                          }
                        />
                    </div>
                    <div className="data-visualization-filters-tag"> 
                      {tags?.map((tag, index) => (
                        <Tag key={`${tag}-${index}`} label={tag.label} variant="secondary" onClick={() => setTags(prev => prev.filter(t => t !== tag))} icon="close"></Tag>
                      ))}
                      {!!sort &&
                        <Tag key={`sort-by`} label={sort.label} variant="secondary" 
                          onClick={() => {
                            setSort(null)}} 
                          icon="close "></Tag>
                      }
                    </div>
                </div>  
            </div>
            <div className="table-wrapper">
              <Table<T>
                onDelete={(uuid) => {
                  setDeleteOpen(true);
                  setUuid(data[uuid].id);
                }}
                onEdit={onEdit}
                dataArray={data}
                columns={columns}
                role={role}
                />
            </div>
              <Drawer
                isOpen={open}
                title={
                  selectedRow != null && selectedRow >= 0
                      ? "Modifier une œuvre"
                      : "Nouvelle œuvre"
                }
                onClose={() => setOpen(false)}
            >
            <EntityForm<T> 
              columns={columns} 
              entity={data[selectedRow]} 
              table={table} 
              onCancel={onCancel} 
              onSubmit={onSubmit} 
              onUpdate={(values) => {
                onUpdate(values)
              }}
              />
            </Drawer>
            <div className="data-visualization-footer">
                <div className="data-visualization-scroll-top" onClick={() => window.scrollTo({top: 0, left: 0, behavior: "smooth"})}>
                  <p className="data-visualization-scroll-top-label">haut de la page</p>
                  <Icon name="arrow-up"></Icon>
                </div>
                <div className="data-visualization-pagination" >
                  <Pagination            
                    page={page} 
                    totalPages={totalPages} 
                    onPageChange={setPage}/>
                </div>
                <div className="data-visualization-max-elements-select">
                  <Dropdown
                    label={`${maxElements} lignes`}
                    options={pageSizes}
                    placement="top"
                    value={String(maxElements)}
                    onChange={(value) => {
                        const pageSize =
                            value === "Tout afficher"
                                ? count
                                : Number(value);

                        setMaxElements(pageSize);
                        setPage(1);
                    }}
                  />
                </div>
            </div>
            <Modal
              isOpen={isDeleteOpen}
              title="Supprimer ces données ?"
              children={
                  <>
                      Cette action est <strong>définitive</strong>.
                      <br />
                      Les données seront supprimées de la base.
                  </>
              }
              variant="destructive"
              onCancel={() => setDeleteOpen(false)}
              onConfirm={() => onDelete(uuid)}
            />
        </div>
       )   
};

export default DataVisualization